import { ChangeEvent, Fragment, FocusEvent } from "react"
import { css, cx } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import {
  AgencyContact,
  AgencyContactPhone,
  MarketAgencyState,
} from "models/agency"
import { WithAuthority } from "components/WithAuthority"
import Button from "ui/buttons/Button"
import {
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from "@material-ui/core"
import assoc from "lodash/fp/set"
import get from "lodash/fp/get"
import debounce from "lodash/fp/debounce"
import { ErrorsMap } from "api/http/utils"
import { TextInput } from "components/inputs/TextInput"

type Props = {
  contact?: AgencyContact
  type: "phones" | "faxes"
  onContactUpdate: (
    type: "phones" | "faxes",
    pld: AgencyContactPhone[],
  ) => Promise<MarketAgencyState>
  onStateUpdate: (type: "phones" | "faxes", pld: AgencyContactPhone[]) => void
  data?: AgencyContactPhone[]
  onClearErrors: (type: string, idx: number) => void
  errors?: Record<string, ErrorsMap | undefined>
  handleValidation: (type: string, idx: number) => void
}

export const ContactNumber = (props: Props) => {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const {
    contact,
    type,
    onContactUpdate,
    onStateUpdate,
    data = [],
    onClearErrors,
    errors,
    handleValidation,
  } = props

  const defaultContact = String(data?.findIndex((s) => s?.default))

  const hasDefaultNumber = data?.some((n) => n?.default)
  const hasEmptyDefaultNumber = data?.some((n) => n?.default && !n?.number)

  const handleChangeDefault = (
    _: ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    const idx = Number(value)
    const isUpdatable = !!data?.[idx]?.number
    const pld = data?.map((n, nidx) => ({
      ...n,
      default: nidx === idx,
    }))
    onStateUpdate(type, pld)
    !!isUpdatable && onContactUpdate(type, pld)
  }

  const handleChange =
    (idx: number) =>
    (
      evt: ChangeEvent<
        | HTMLInputElement
        | {
            name?: string | undefined
            value: unknown
          }
      >,
    ) => {
      const { name, value } = evt?.target
      const s = data
      !!name && onStateUpdate(type, assoc(name, value, s))
      !!errors?.[type]?.[idx] && onClearErrors(type, idx)
    }

  const handleBlur =
    (idx: number) =>
    (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name = "", value = "" } = evt?.target ?? {}
      const isDefault = data?.find((s, sidx) => sidx === idx)?.default
      if (
        !!value &&
        value !== get(name, contact?.[type]?.[idx]) &&
        !hasEmptyDefaultNumber
      ) {
        onContactUpdate(type, data)
      }
      if (!value && isDefault) {
        handleValidation(type, idx)
      }
      if (hasEmptyDefaultNumber) {
        const emptyDefaultNumberIdx = data?.findIndex(
          (n) => n?.default && !n?.number,
        )
        handleValidation(type, emptyDefaultNumberIdx)
      }
    }

  const handleAdd = () => {
    const newEntry = { number: "", default: !hasDefaultNumber }
    const s = [...data]
    onStateUpdate(type, [...s, newEntry])
  }

  const handleDelete = (idx: number) => {
    const remaining = data?.filter((_, id) => id !== idx) ?? []
    if (data?.length === 1) {
      onStateUpdate(type, [])
      onContactUpdate(type, [])
      !!errors?.[type]?.[idx] && onClearErrors(type, idx)
    }
    if (!hasEmptyDefaultNumber && data?.length !== 1) {
      onStateUpdate(type, remaining)
    }
    if (data?.length === contact?.[type]?.length && !hasEmptyDefaultNumber) {
      onContactUpdate(type, remaining)
    }
  }

  const canIAddContactDetails =
    !data?.length || !!data?.[data?.length - 1]?.number

  return (
    <div className={styles.contactDetails}>
      <div className={styles.contactWrapper}>
        <FormControl>
          <RadioGroup
            aria-labelledby="agency-contact-radio-buttons-group-label"
            name="radio-buttons-group"
            onChange={debounce(300, handleChangeDefault)}
            value={defaultContact || ""}
          >
            {data?.length ? (
              data?.map((phone, idx) => (
                <Fragment key={idx}>
                  <FormControlLabel
                    value={`${idx}`}
                    control={<Radio color="primary" />}
                    classes={{ root: styles.radio, label: styles.contact }}
                    name="default"
                    disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
                    label={
                      <>
                        <TextInput
                          id="agency_contact"
                          name={`${idx}.number`}
                          value={phone?.number || ""}
                          onChange={handleChange(idx)}
                          onBlur={handleBlur(idx)}
                          label={t(`market-agencies.profile.${type}`)}
                          className={styles.phoneInput}
                          margin="none"
                          size="medium"
                          disabled={!hasAuthority(["AUTH_EDIT_AGENCY"])}
                        />
                        {!phone?.default || data?.length === 1 ? (
                          <WithAuthority allowed={["AUTH_EDIT_AGENCY"]}>
                            <IconButton
                              className={styles.iconButton}
                              onClick={debounce(300, () => handleDelete(idx))}
                            >
                              <DeleteIcon
                                className={styles.deleteIcon}
                                color="inherit"
                              />
                            </IconButton>
                          </WithAuthority>
                        ) : null}
                      </>
                    }
                  />
                  {!!errors?.[type]?.[idx] ? (
                    <p className={cx(styles.error, "BodyBody-xsRedRegular")}>
                      {t(errors?.[type]?.[idx] ?? "")}
                    </p>
                  ) : (
                    <span />
                  )}
                </Fragment>
              ))
            ) : (
              <div className={cx(styles.empty, "BodyBody-2Dark-greyLight")}>
                {t(`market-agencies.profile.no.${type}`)}
              </div>
            )}
          </RadioGroup>
        </FormControl>
        <WithAuthority allowed={["AUTH_EDIT_AGENCY"]}>
          <Button
            color="primary"
            className={styles.textButton}
            onClick={handleAdd}
            disabled={!canIAddContactDetails}
          >
            {`+ ${t(`market-agencies.profile.add-${type}`)}`}
          </Button>
        </WithAuthority>
      </div>
    </div>
  )
}

export const styles = {
  contactDetails: css`
    padding-top: var(--spacing-s);
  `,
  contactWrapper: css`
    display: flex;
    flex-direction: column;
    margin-bottom: var(--spacing-xxl);
  `,
  radio: css`
    display: flex;
    align-items: center;
  `,
  phoneInput: css`
    width: 208px;
  `,
  iconButton: css`
    &.MuiIconButton-root {
      margin-left: var(--spacing-xs);
    }
  `,
  deleteIcon: css`
    width: 20px;
    height: 20px;
    color: var(--disabled-grey);
    cursor: pointer;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: bold;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    &[aria-disabled="true"] {
      background-color: transparent;
    }
  `,
  contact: css`
    display: flex;
    align-items: center;
    padding: var(--spacing-s) 0;
  `,
  error: css`
    padding: 0 28px 12px;
  `,
  empty: css`
    padding: var(--spacing-s) 0;
  `,
}
